import React, {memo} from 'react';
import styled from 'styled-components';
import {TscStyles, TscButtonType, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import ProgressLoader from '../../util/ProgressLoader';
import '../../../static/css/modal.less';
import {useTscTheme} from '../../../context/themeProvider';
import {AlertModalPortal, BasicModalPortal} from '../../util/ModalPortalHelper';

const TextContainer = styled.span`
   display: flex;
   justify-content: center;
`;

export const UploadProgressModalBase: React.FC<IUploadProgressModalProps> = ({uploadProgress, onClose, cancelUpload}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [state, setState] = React.useState({
      errorText: '',
      warnAboutCancel: false
   } as IUploadProgressModalState);

   const updateState = (updatedValues: Partial<IUploadProgressModalState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const onModalCloseClick = () => {
      showCancelWarn();
   };

   const onCancelWarnConfirm = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      updateState({warnAboutCancel: false});
      cancelUpload();
      onClose();
   };

   const onCancelWarnClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      updateState({warnAboutCancel: false});
   };

   const showCancelWarn = () => {
      updateState({warnAboutCancel: true});
   };

   const progressModal: React.JSX.Element = (
      <BasicModalPortal
         visible={true}
         title={t('uploadMedia.title')}
         onClose={onModalCloseClick}
         themeName={theme}
         testId="upload-progress-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <TextContainer>
                  {t('uploadMedia.message')}
               </TextContainer>
               <ProgressLoader
                  containerStyle={{width: '90%', margin: '0.75rem auto', background: theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.lightMedium : TscStyles.color.ui.dawn.medium}}
                  uploadProgress={uploadProgress}
               />
            </div>
         </div>
      </BasicModalPortal>
   );

   const cancelUploadAlert: React.JSX.Element = (
      <AlertModalPortal
         onConfirm={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onCancelWarnConfirm(e)}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('uploadMedia.cancel.title')}
         onDismiss={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onCancelWarnClose(e)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.back')}
         onClose={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onCancelWarnClose(e)}
         title={t('uploadMedia.cancel.title')}
      >
         {t('uploadMedia.cancel.warning')}
      </AlertModalPortal>
   );

   return (
      <>
         {progressModal}
         {state.warnAboutCancel && cancelUploadAlert}
      </>
   );
};

interface IUploadProgressModalState {
   errorText: string;
   warnAboutCancel: boolean;
}

export interface IUploadProgressModalProps {
   uploadProgress: number;
   cancelUpload(): void;
   onClose(): void;
}

export default memo(UploadProgressModalBase);
